import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { avatarSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PostCard from "../components/PostCard";
import { VerifiedSpotlight } from "../components/VerifiedSpotlight";
import { RightRail } from "../components/Layout";
import { Loader2, ImagePlus, Link2, Sparkles, Compass, BookOpen } from "lucide-react";

const PAGE = 8;
const FEED_CACHE = { items: null, hasMore: true, seen: [], scrollY: 0 };

function Composer() {
  const { user } = useAuth();
  const nav = useNavigate();
  if (!user) {
    return (
      <div data-testid="feed-guest-cta" className="bg-[#182132] border border-white/10 rounded-xl p-6 mb-5 text-center">
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#38BDF8] mb-2">A living archive · 18+</p>
        <p className="text-white font-heading text-2xl tracking-tight mb-2">Queer erotic history, preserved</p>
        <p className="text-[#94A3B8] text-sm mb-5 max-w-md mx-auto">KinkVault is a community-built archive of gay kink &amp; fetish culture — uploaded, understood and made searchable so it's never lost. Preservation, not just porn.</p>
        <div className="flex gap-2 justify-center">
          <Link to="/register" className="px-5 py-2 rounded-lg bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold text-sm transition-colors">Join the archive</Link>
          <Link to="/about" className="px-5 py-2 rounded-lg bg-white/[0.06] hover:bg-white/10 text-white font-semibold text-sm transition-colors">What is this?</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-[#182132] border border-white/10 rounded-xl p-3 sm:p-4 mb-5">
      <div className="flex items-center gap-3">
        {user.avatar_url ? (
          <img src={avatarSrc(user.avatar_url)} alt="" className="w-10 h-10 rounded-full object-cover bg-[#1B2437]" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#0066FF] flex items-center justify-center text-white font-semibold">{user.username?.[0]?.toUpperCase()}</div>
        )}
        <button data-testid="composer-open" onClick={() => nav("/upload")} className="flex-1 text-left px-4 py-2.5 rounded-full bg-[#111827] hover:bg-[#1B2437] text-[#94A3B8] transition-colors">
          What's your kink today, {user.display_name || user.username}?
        </button>
      </div>
      <div className="h-px bg-white/[0.07] my-3" />
      <div className="flex items-center justify-around">
        <button data-testid="composer-upload" onClick={() => nav("/upload")} className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/[0.06] text-[#94A3B8] font-semibold text-sm transition-colors">
          <ImagePlus size={19} className="text-[#0066FF]" /> Photo / Video
        </button>
        <button data-testid="composer-import" onClick={() => nav("/import")} className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/[0.06] text-[#94A3B8] font-semibold text-sm transition-colors">
          <Link2 size={19} className="text-[#38BDF8]" /> Import URL
        </button>
      </div>
    </div>
  );
}

const QUICK = [
  { to: "/explore", icon: Compass, label: "Explore" },
  { to: "/foryou", icon: Sparkles, label: "For You" },
  { to: "/stories", icon: BookOpen, label: "Stories" },
];

export default function Feed() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const seenRef = useRef(new Set());
  const sentinel = useRef(null);

  const fetchBatch = useCallback(async (reset) => {
    const exclude = reset ? "" : Array.from(seenRef.current).slice(-200).join(",");
    const { data } = await api.get("/feed", { params: { exclude, limit: PAGE } });
    return data;
  }, []);

  useEffect(() => {
    let alive = true;
    if (FEED_CACHE.items && FEED_CACHE.items.length) {
      seenRef.current = new Set(FEED_CACHE.seen);
      setItems(FEED_CACHE.items);
      setHasMore(FEED_CACHE.hasMore);
      setLoading(false);
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, FEED_CACHE.scrollY || 0)));
      return () => { alive = false; };
    }
    setLoading(true);
    seenRef.current = new Set();
    fetchBatch(true).then((data) => {
      if (!alive) return;
      data.forEach((d) => seenRef.current.add(d.id));
      setItems(data);
      setHasMore(data.length === PAGE);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [fetchBatch]);

  useEffect(() => {
    if (items.length) { FEED_CACHE.items = items; FEED_CACHE.hasMore = hasMore; FEED_CACHE.seen = Array.from(seenRef.current); }
  }, [items, hasMore]);
  useEffect(() => {
    const onScroll = () => { FEED_CACHE.scrollY = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) return;
    setLoadingMore(true);
    try {
      const data = await fetchBatch(false);
      const fresh = data.filter((d) => !seenRef.current.has(d.id));
      fresh.forEach((d) => seenRef.current.add(d.id));
      setItems((prev) => [...prev, ...fresh]);
      setHasMore(data.length === PAGE && fresh.length > 0);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, loading, hasMore, fetchBatch]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const obs = new IntersectionObserver((e) => { if (e[0].isIntersecting) loadMore(); }, { rootMargin: "800px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  return (
    <div className="max-w-[1000px] mx-auto px-2 sm:px-4 py-5" data-testid="home-dashboard">
      <div className="flex justify-center gap-8">
        {/* Tumblr single-column dashboard */}
        <div className="w-full max-w-[580px]" data-testid="feed-column">
          <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
            {QUICK.map((q) => (
              <Link key={q.to} to={q.to} data-testid={`dash-quick-${q.label.toLowerCase().replace(" ", "")}`}
                className="flex items-center gap-1.5 shrink-0 px-3.5 py-1.5 rounded-full bg-[#182132] border border-white/10 text-[#94A3B8] text-xs font-semibold hover:text-white hover:border-[#0066FF]/40 transition-colors">
                <q.icon size={14} className="text-[#0066FF]" /> {q.label}
              </Link>
            ))}
          </div>

          <Composer />
          <VerifiedSpotlight />

          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#0066FF]" size={30} /></div>
          ) : items.length === 0 ? (
            <div className="bg-[#182132] border border-white/10 rounded-xl p-10 text-center">
              <Sparkles size={30} className="text-[#0066FF] mx-auto mb-3" />
              <p className="text-white font-semibold text-lg mb-1">Your dashboard is quiet</p>
              <p className="text-[#94A3B8] text-sm mb-5">{user ? "Follow some archivists or drop the first post." : "Sign in to build your dashboard."}</p>
              <Link to={user ? "/upload" : "/register"} className="inline-block px-6 py-2.5 rounded-lg bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold text-sm transition-colors">{user ? "Upload media" : "Join the archive"}</Link>
            </div>
          ) : (
            <>
              {items.map((item) => <PostCard key={item.id} item={item} />)}
              <div ref={sentinel} className="h-14 flex items-center justify-center">
                {loadingMore && <Loader2 className="animate-spin text-[#0066FF]" size={22} />}
                {!hasMore && !loadingMore && <span className="text-[#64748B] text-xs uppercase tracking-widest">— you're all caught up —</span>}
              </div>
            </>
          )}
        </div>

        <aside className="hidden xl:block w-[320px] shrink-0 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto custom-scrollbar">
          <RightRail />
        </aside>
      </div>
    </div>
  );
}
